import { useEffect, useState } from 'react';
import { purchasingAPI, qualityAPI, PurchaseOrderLine, NonConformanceReport } from '../../lib/api';
import { lineIsFinished } from './tabs/po-lines/utils';
import { ChangelogEntry } from '../common/Changelog';
import { changeActorName, countChanges } from './changelogFields';
import type { WorkflowActionPayload } from './AcknowledgmentSharedUtils';
import { isHeld, mayReviewProposal } from './pendingProposal';
import type { HoldNextAction } from './HoldNextActionChoice';
import type { SetDatesPayload } from './SetDatesModal';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/errorUtils';

export type { WorkflowAction, WorkflowActionPayload } from './AcknowledgmentSharedUtils';

/**
 * CR-6409: a tab count the modal can do without. A failed count must not take
 * the line down with it — the tab label just loses its number.
 */
const countOrNull = <T>(request: Promise<T>, what: string): Promise<T | null> =>
  request.catch((err: unknown) => {
    console.warn(`Could not load the ${what} count for the line:`, err);
    return null;
  });

interface UsePOLineDetailArgs {
  lineId: number;
  isOpen: boolean;
  userType: 'customer' | 'supplier';
  /** The authenticated user, for the platform checks in the visibility rules. */
  user: { platform?: string } | null | undefined;
  /** True when the tenant negotiates on schedule rows instead of the PO line. */
  scheduleCollaborationActive: boolean;
  /** Site-scoped write check from UserPermissionsContext. */
  hasPurchasingWriteAccess: (siteId?: number) => boolean;
  onUpdate?: () => void;
}

/**
 * Everything the PO Line Detail modal does, as opposed to everything it draws.
 *
 * Lifted out of POLineDetailModal verbatim to bring that file back under the
 * 500-line limit (CR-7258 touched it, so the limit applies). It owns the line,
 * its changelog, the GRN count and the load state, because every function here
 * reads or writes them; the modal keeps only its own presentational state.
 *
 * @returns The loaded line and changelog, plus the workflow actions and the
 *   two visibility predicates the header and footer consult.
 */
export function usePOLineDetail({
  lineId,
  isOpen,
  userType,
  user,
  scheduleCollaborationActive,
  hasPurchasingWriteAccess,
  onUpdate,
}: UsePOLineDetailArgs) {
  const [line, setLine] = useState<PurchaseOrderLine | null>(null);
  const [changelogs, setChangelogs] = useState<ChangelogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // CR-4699: GRN count for tab label
  const [grnCount, setGrnCount] = useState<number>(0);
  // Customer review 11 Sep (item 30a/b): the line's NCRs, from the same
  // role-scoped endpoint the NCR tab lists — drives the "NCR (n)" tab label
  // and the NCR entries in the Audit History. Null = could not be loaded.
  const [ncrs, setNcrs] = useState<NonConformanceReport[] | null>(null);
  // CR-6409: the ASN/Shipments count and the Communication unread pill. Null =
  // could not be loaded, so the label shows no number rather than a wrong one.
  const [asnCount, setAsnCount] = useState<number | null>(null);
  const [unreadMessageCount, setUnreadMessageCount] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen && lineId) {
      fetchLineDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, lineId]);

  interface RawChangelog {
    id: number; change_date: string; field_changed: string; old_value: string; new_value: string;
    changed_by_details?: { full_name?: string; companyName?: string };
    reason?: string; change_type?: string; event_type?: string;
    // CR-4712 round 2 rework: previous version of this transformer read
    // `changed_by_company_name` (with the _name suffix) and
    // `changed_by_details.companyName` — neither field exists on the
    // backend payload. The serializer returns `changed_by_company`
    // (resolved string from get_changed_by_company) directly. The
    // mismatched key meant this transformer always produced
    // `changed_by_company: undefined`, the Changelog row renderer fell
    // through its `entry.changed_by_company ? ... : ''` ternary, and
    // the audit row rendered as a bare actor name in production —
    // exactly the regression Sarah reported. Read the actual field.
    changed_by_company?: string;
    // CR-4713 round 2 rework: surface the impersonation actor + their
    // company so the Changelog row renderer can append the
    // "(acting as <Company> · <Name>)" suffix.
    acting_as_user_details?: { id: number; first_name?: string; last_name?: string; full_name?: string; email?: string } | null;
    acting_as_company?: string | null;
    // CR-4865: carry the change-request grouping key through to the tab renderer.
    change_request_ref?: number | null;
    po_line?: number | null;
    // CR-4713 round 4: the side of the table the row was written for.
    company_type?: 'customer' | 'supplier' | '';
  }
  const transformChangelogs = (results: RawChangelog[]): ChangelogEntry[] =>
    results.map((log) => ({
      id: log.id, change_date: log.change_date,
      // EXPEDITING-3: the person who actually did it (see changeActorName).
      changed_by: changeActorName(log),
      changed_by_company: log.changed_by_company,
      acting_as_user_details: log.acting_as_user_details,
      acting_as_company: log.acting_as_company,
      field_changed: log.field_changed, old_value: log.old_value, new_value: log.new_value,
      reason: log.reason, change_type: log.change_type, event_type: log.event_type,
      change_request_ref: log.change_request_ref ?? null,
      company_type: log.company_type,
    }));

  const fetchLineDetails = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      const [lineResponse, changelogResponse, grnResponse, ncrResponse, asnResponse, unreadResponse] = await Promise.all([
        purchasingAPI.getLine(lineId),
        // NO page params here, deliberately. `POChangelogViewSet.list` returns
        // a PO line's trace UNPAGINATED so it is guaranteed complete
        // (po_changelog_views.py), and switches to pagination the moment any
        // page param appears. Round 2 added `page_size: 50` to protect the
        // OTIF badge's who/when/why from DRF's default page of 20 — but that
        // default never applied to this caller, so the "fix" would have been
        // the only thing capping a feed that had no cap. Round 3 removed it.
        purchasingAPI.getChangelogs({ po_line: lineId, ordering: '-change_date' }),
        purchasingAPI.getLineGRNSummary(lineId),
        // An NCR failure (e.g. no quality access) must not take the whole
        // modal down; the tab label just loses its count.
        // CR-7311 AC3: `po_line_scope` is the shared rule CR-7306's per-line
        // NCR badge counts on (backend `ncr_rejection.rejecting_ncrs`) --
        // the line's own NCRs plus the ones raised against a receipt of it,
        // without drafts, deleted or internal. `po_line=` was a bare FK
        // match, so the pop-up's tab label, its NCR tab and its Audit History
        // could all show a different set from the PO header's Lines panel.
        qualityAPI.getNCRs({ po_line_scope: lineId, page_size: 50 }).catch((ncrErr: unknown) => {
          console.warn('Could not load NCRs for the line:', ncrErr);
          return null;
        }),
        // CR-6409: the same endpoint the ASN/Shipments tab lists, so the
        // count is the number of rows the viewer sees there.
        countOrNull(purchasingAPI.getLineASNs(lineId), 'ASN'),
        countOrNull(purchasingAPI.getLineUnreadMessageCount(lineId), 'unread message'),
      ]);

      setLine(lineResponse.data);
      setChangelogs(transformChangelogs(changelogResponse.data.results));
      // CR-4699: Store GRN count for tab label
      setGrnCount(grnResponse.data.total_grns);
      setNcrs(ncrResponse ? ncrResponse.data.results || [] : null);
      setAsnCount(asnResponse ? asnResponse.data.total_asns : null);
      setUnreadMessageCount(unreadResponse ? unreadResponse.data.unread_count : null);
    } catch (err: unknown) {
      console.error('Failed to fetch line details:', err);
      if (!silent) {
        const axiosError = err as { response?: { data?: { detail?: string } } };
        setError(axiosError.response?.data?.detail || 'Failed to load line details');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  /**
   * CR-6409: re-read the unread pill. The Communication tab calls this once it
   * has marked the other side's messages read, so the pill clears while the
   * thread is on screen rather than on the next open.
   */
  const refreshUnreadMessageCount = async () => {
    const response = await countOrNull(purchasingAPI.getLineUnreadMessageCount(lineId), 'unread message');
    if (response) setUnreadMessageCount(response.data.unread_count);
  };

  /**
   * The count beside a field in the modal. The RULES live in
   * changelogFields.countChanges — a pure function over rows, so the two Aaron
   * asked for on the walkthrough (admin corrections do not count; a hold counts
   * its ON transitions) are testable without standing a fetching hook up (the
   * test lens, gate round 1).
   */
  const getChangeCount = (fieldName: string) => countChanges(changelogs, fieldName);

  const handleToggleCritical = async () => {
    if (!line) return;
    try {
      const newCriticalState = !line.is_critical;
      const response = await purchasingAPI.setLineCritical(line.id, newCriticalState);
      if (response.data.status === 'success') {
        toast.success(response.data.message);
        setLine(prev => prev ? { ...prev, is_critical: newCriticalState } : null);
        const changelogResponse = await purchasingAPI.getChangelogs({ po_line: lineId, ordering: '-change_date' });
        setChangelogs(transformChangelogs(changelogResponse.data.results));
        onUpdate?.();
      } else {
        // A 200 that does not say `success` used to do nothing at all — no toast,
        // no refresh, a control that looked broken (silent-failure lens, round 2).
        toast.error(response.data.message || 'Could not update the critical flag');
      }
    } catch (error: unknown) {
      console.error('Failed to toggle critical:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update critical status';
      toast.error(errorMessage);
    }
  };

  const handleToggleHold = async (reason?: string, nextAction?: HoldNextAction) => {
    if (!line) return;
    try {
      let response;
      if (line.on_hold) {
        response = await purchasingAPI.releaseLineHold(line.id);
      } else {
        if (!reason) return;
        // G7: the nomination rides with the reason. Absent (an older caller),
        // the service defaults it to the placer's own side — the old behaviour.
        response = await purchasingAPI.putLineOnHold(line.id, {
          reason,
          ...(nextAction ? { nextActionWith: nextAction } : {}),
        });
      }
      if (response.data.status === 'success') {
        toast.success(response.data.message);
        await fetchLineDetails();
        onUpdate?.();
      } else {
        toast.error(response.data.message || 'Could not update the hold');
      }
    } catch (err: unknown) {
      console.error('Failed to toggle hold:', err);
      const axiosError = err as { response?: { data?: { message?: string } } };
      toast.error(axiosError.response?.data?.message || 'Failed to update hold status');
    }
  };

  /**
   * P10: the customer writes the three dates directly, while the line is `new`.
   *
   * Not a proposal and not a toggle, so it gets its own handler rather than a
   * branch inside handleWorkflowAction — the server refuses it outright once
   * the supplier has answered, and the error it returns says so in words worth
   * putting in front of the user rather than replacing.
   */
  const handleSetDates = async (payload: SetDatesPayload) => {
    if (!line) return;
    try {
      const response = await purchasingAPI.setLineDates(line.id, payload);
      if (response.data.status === 'success') {
        toast.success(response.data.message);
        await fetchLineDetails();
        onUpdate?.();
      } else {
        // THROWS, unlike its siblings: this handler's caller closes the modal on a
        // resolved promise, so a silent non-success closed Update Dates over dates
        // that were never written.
        const message = response.data.message || 'Could not update the dates';
        toast.error(message);
        throw new Error(message);
      }
    } catch (err: unknown) {
      console.error('Failed to set dates:', err);
      const axiosError = err as { response?: { data?: { message?: string } } };
      toast.error(axiosError.response?.data?.message || 'Failed to update dates');
      throw err;
    }
  };

  const handleWorkflowAction = async (data: WorkflowActionPayload) => {
    try {
      let response;

      switch (data.action) {
        case 'accept':
          response = await purchasingAPI.acceptDeliveryDate(data.lineId, { comments: data.comments });
          break;
        case 'decline':
          response = await purchasingAPI.declineDeliveryDate(data.lineId, { reason: data.reason ?? '', comments: data.comments });
          break;
        case 'propose':
          response = await purchasingAPI.proposeAlternativeDate(data.lineId, { proposedDate: data.proposedDate ?? '', reason: data.reason ?? '', comments: data.comments });
          break;
        case 'proposeQuantity':
          response = await purchasingAPI.proposeQuantityChange(data.lineId, { proposedQuantity: String(data.proposedQuantity ?? 0), reason: data.reason ?? '', comments: data.comments });
          break;
        case 'proposePrice':
          response = await purchasingAPI.proposePriceChange(data.lineId, { proposedPrice: String(data.proposedPrice ?? 0), reason: data.reason ?? '', comments: data.comments });
          break;
        // G12: every leg of a mixed proposal in one request, so a refusal on one
        // takes the others back rather than leaving half of it committed.
        case 'proposeChanges':
          response = await purchasingAPI.proposeChanges(data.lineId, {
            changes: data.changes ?? [], comments: data.comments,
          });
          break;
        default:
          throw new Error(`Unknown action: ${data.action}`);
      }

      if (response?.data?.message) {
        toast.success(response.data.message);
      }

      // CR-4723: Upload attached files as PO documents after action succeeds.
      // Use other_supplier / other_customer — PODocument has no change_request choice.
      if (data.files && data.files.length > 0 && line) {
        const docType = userType === 'supplier' ? 'other_supplier' : 'other_customer';
        let failedCount = 0;
        let firstMessage: string | null = null;
        for (const file of data.files) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('po', String(line.po));
          formData.append('po_line', String(line.id));
          formData.append('document_type', docType);
          formData.append(
            'description',
            `Change request attachment (${data.action}): ${file.name}`,
          );
          try {
            await purchasingAPI.uploadPODocument(formData);
          } catch (uploadErr: unknown) {
            failedCount += 1;
            console.error('Failed to upload attachment:', uploadErr);
            if (!firstMessage) {
              firstMessage = getApiErrorMessage(uploadErr, 'Upload failed');
            }
          }
        }
        if (failedCount > 0) {
          toast.error(
            failedCount === data.files.length
              ? `Attachments could not be uploaded: ${firstMessage ?? 'Unknown error'}`
              : `${failedCount} of ${data.files.length} attachments failed. ${firstMessage ?? ''}`,
          );
        }
      }

    } catch (error: unknown) {
      console.error('Workflow action failed:', error);
      const axiosError = error as { response?: { data?: { message?: string } } };
      const errorMessage = axiosError.response?.data?.message || 'Workflow action failed';
      toast.error(errorMessage);
      throw error;
    }

    // CR-4860: Side effects must not propagate to the caller. Previously,
    // fetchLineDetails / onUpdate ran inside the try block above; if the
    // action API succeeded (success toast already fired) but the post-call
    // refetch errored, the throw bubbled up to the caller. ProposeChangeModal
    // would catch it and skip its own onClose(), leaving the modal open
    // even though the propose itself succeeded. Run the refresh outside
    // the try, swallow refetch errors, so the propose flow can close.
    // CR-4883: also swallow errors from the parent's onUpdate callback —
    // if onUpdate throws (e.g. parent re-render quirk), the throw used to
    // bubble back to ProposeChangeModal and skip its own onClose, so the
    // modal stayed open even though the propose itself succeeded.
    try {
      await fetchLineDetails();
    } catch (refetchErr) {
      console.warn('Workflow action succeeded but refetch failed:', refetchErr);
    }
    try {
      onUpdate?.();
    } catch (updateErr) {
      console.warn('Workflow action succeeded but onUpdate failed:', updateErr);
    }
  };

  // CR-4203: Check if workflow actions should be shown
  const canShowWorkflowActions = (): boolean => {
    if (!line || !user || !user.platform) return false;
    // CR-4697 + CR-7418: nothing applies to a finished line, closed OR
    // cancelled. Through the grid's predicate, because this modal and that
    // grid disagreeing is the bug: a cancelled line offered Accept and Propose
    // here and neither there, and the server 400s both.
    if (lineIsFinished(line)) return false;
    // Schedule-aware tenants negotiate at the schedule level only.
    if (scheduleCollaborationActive) return false;

    const status = line.acknowledge_status;
    const isCustomer = user.platform === 'CUSTOMER' || user.platform === 'HELIX';
    const isSupplier = user.platform === 'SUPPLIER';

    // CR-4647: Site-based write access only applies to customer users.
    // Suppliers always have write access to their own PO lines.
    if (isCustomer && !hasPurchasingWriteAccess(line.customer_id)) return false;

    // EXPEDITING-PARITY PR 2: a held line is out of the negotiation entirely,
    // and `change_declined` no longer re-offers Accept/Reject to the side that
    // DECLINED — which is what this modal used to do while the grid beside it
    // offered nothing, and legacy offered it to whoever RAISED the change.
    // Aaron's ruling (17 Sep, option C): a decline that carries a counter is a
    // proposal and never lands here; a decline without one closes the request,
    // and a fresh Propose is the only way forward. One rule, four surfaces —
    // mayReviewProposal in pendingProposal.ts.
    if (isHeld(line)) return false;

    // The supplier's answer to a NEW order: an acknowledgement, not a review.
    if (isSupplier && status === 'new') return true;

    return mayReviewProposal(line, isSupplier ? 'supplier' : isCustomer ? 'customer' : null);
  };

  // CR-4203: Check if propose change should be shown
  const canShowProposeChange = (): boolean => {
    if (!line || !user || !user.platform) return false;
    // CR-4697 + CR-7418: nothing applies to a finished line, closed OR
    // cancelled. Through the grid's predicate, because this modal and that
    // grid disagreeing is the bug: a cancelled line offered Accept and Propose
    // here and neither there, and the server 400s both.
    if (lineIsFinished(line)) return false;
    // Schedule-aware tenants negotiate at the schedule level only.
    if (scheduleCollaborationActive) return false;

    const status = line.acknowledge_status;
    const isCustomer = user.platform === 'CUSTOMER' || user.platform === 'HELIX';
    const isSupplier = user.platform === 'SUPPLIER';

    // CR-4647: Site-based write access only applies to customer users.
    if (isCustomer && !hasPurchasingWriteAccess(line.customer_id)) return false;

    // EXPEDITING-PARITY PR 2: nothing is proposable on a held line, and after a
    // decline EITHER side may propose afresh — the grid has always said so
    // (usePOLinesActions.shouldShowProposeChange); this modal restricted it to
    // whoever raised the declined change, so the same line offered Propose in
    // the list and not in the modal.
    if (isHeld(line)) return false;

    const OPEN_TO_PROPOSE = ['new', 'accepted', 'wip', 'change_declined'];
    if (isCustomer || isSupplier) return OPEN_TO_PROPOSE.includes(status || '');

    return false;
  };

  return {
    line,
    changelogs,
    loading,
    error,
    grnCount,
    ncrs,
    asnCount,
    unreadMessageCount,
    refreshUnreadMessageCount,
    fetchLineDetails,
    getChangeCount,
    handleToggleCritical,
    handleToggleHold,
    handleSetDates,
    handleWorkflowAction,
    canShowWorkflowActions,
    canShowProposeChange,
  };
}
